/**
 * Season agenda helpers for the ProgramBuilder season strip
 * (docs/program-builder-season-strip/ANALYSIS.md §3).
 *
 * Pure functions over ISO `YYYY-MM-DD` strings. A program has no start date:
 * a week sits on the calendar only through its day dates, which skeleton weeks
 * keep under `day_dates`, so everything here works before week content loads.
 */
import {
	getWeekDayDates,
	getWeekMondayFromDate,
	parseLocalDate,
	toLocalISODate,
} from "./programBuilderUtils";

const PRIORITY_RANK = { A: 0, B: 1, C: 2 };

export const shiftISODate = (iso, days) => {
	const date = parseLocalDate(iso);
	date.setDate(date.getDate() + days);
	return toLocalISODate(date);
};

export const daysBetween = (fromISO, toISO) => {
	const from = parseLocalDate(fromISO);
	const to = parseLocalDate(toISO);
	return Math.round((to - from) / 86400000);
};

export const rangesOverlap = (aStart, aEnd, bStart, bEnd) =>
	Boolean(aStart && aEnd && bStart && bEnd) && aStart <= bEnd && aEnd >= bStart;

/** `{ start, end }` of a week from its day dates, or `null` when undated. */
export const getWeekDateRange = (week) => {
	const dates = getWeekDayDates(week).slice().sort();
	if (dates.length === 0) return null;
	return { start: dates[0], end: dates[dates.length - 1] };
};

/**
 * The calendar weeks a program week occupies: Monday of its first day through
 * Sunday of its last one.
 *
 * Matching races and phases against the *content* range (`getWeekDateRange`)
 * loses everything that falls on a day the week has no session on — a Sunday
 * race on a Mon-Fri week belonged to no week at all and its trophy appeared
 * nowhere. The content range stays the one we display: it says what the week
 * actually holds.
 */
export const getWeekCalendarRange = (week) => {
	const range = getWeekDateRange(week);
	if (!range) return null;
	const monday = getWeekMondayFromDate(range.start);
	if (!monday) return range;
	const sunday = getWeekMondayFromDate(range.end);
	sunday.setDate(sunday.getDate() + 6);
	return { start: toLocalISODate(monday), end: toLocalISODate(sunday) };
};

const projectedWeek = (monday, offsetDays) => {
	const start = shiftISODate(monday, offsetDays);
	return { start, end: shiftISODate(start, 6), projected: true };
};

/**
 * Calendar range of every week of the program, undated weeks included.
 *
 * A week just added has no day yet, so `getWeekCalendarRange` knows nothing
 * about it and its phase only appeared once a day was added. But that first
 * day's date is already decided: `getWeekAnchorMonday` places it after the
 * nearest dated week before it (or before the nearest one after it), one
 * calendar week per step. Same rule here, so the band shows the phase the
 * week will fall in as soon as it exists. A program with no dated day at all
 * stays without ranges: there is nothing to place it against. Projected
 * ranges carry `projected: true`.
 */
export const getWeekCalendarRanges = (weeks) => {
	const own = (weeks || []).map(getWeekCalendarRange);
	return own.map((range, index) => {
		if (range) return range;
		for (let idx = index - 1; idx >= 0; idx -= 1) {
			if (own[idx]) return projectedWeek(own[idx].start, (index - idx) * 7);
		}
		for (let idx = index + 1; idx < own.length; idx += 1) {
			if (own[idx]) return projectedWeek(own[idx].start, -(idx - index) * 7);
		}
		return null;
	});
};

/** Whole-program range padded on both sides, used to bound the agenda fetch. */
export const getProgramDateRange = (weeks, paddingDays = 14) => {
	let start = null;
	let end = null;
	getWeekCalendarRanges(weeks).forEach((range) => {
		if (!range) return;
		if (!start || range.start < start) start = range.start;
		if (!end || range.end > end) end = range.end;
	});
	if (!start || !end) return null;
	return {
		start: shiftISODate(start, -paddingDays),
		end: shiftISODate(end, paddingDays),
	};
};

const mesocycleEnd = (meso) => meso?.end_date || meso?.start_date;
const competitionEnd = (event) => event?.end_date || event?.date;

/** Mesocycles intersecting the range, earliest first. */
export const mesocyclesForRange = (mesocycles, range) => {
	if (!range) return [];
	return (mesocycles || [])
		.filter((meso) =>
			rangesOverlap(meso?.start_date, mesocycleEnd(meso), range.start, range.end),
		)
		.sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
};

/**
 * One row per athlete per race is how competitions are stored; the backend
 * derives `shared_group_key` so the same race shows once. Legacy rows without
 * the key fall back to the same normalised fingerprint.
 */
export const competitionClusterKey = (event) => {
	if (event?.shared_group_key) return event.shared_group_key;
	const norm = (value) => (value || "").trim().toLowerCase().replace(/\s+/g, " ");
	return `${norm(event?.name)}|${event?.date || ""}|${norm(event?.location)}`;
};

export const clusterCompetitions = (events) => {
	const clusters = new Map();
	(events || []).forEach((event) => {
		if (!event?.date) return;
		const key = competitionClusterKey(event);
		const existing = clusters.get(key);
		if (existing) {
			existing.events.push(event);
			if (event.athlete_id && !existing.athleteIds.includes(event.athlete_id)) {
				existing.athleteIds.push(event.athlete_id);
			}
			const rank = PRIORITY_RANK[event.priority] ?? 9;
			if (rank < (PRIORITY_RANK[existing.priority] ?? 9)) {
				existing.priority = event.priority;
			}
			return;
		}
		clusters.set(key, {
			key,
			name: event.name || "",
			date: event.date,
			end_date: event.end_date || null,
			location: event.location || null,
			event_type: event.event_type || null,
			priority: event.priority || null,
			athleteIds: event.athlete_id ? [event.athlete_id] : [],
			events: [event],
		});
	});
	return Array.from(clusters.values()).sort((a, b) =>
		a.date.localeCompare(b.date),
	);
};

/** Clusters (or raw events) intersecting the range, earliest first. */
export const competitionsForRange = (competitions, range) => {
	if (!range) return [];
	return (competitions || [])
		.filter((event) =>
			rangesOverlap(event?.date, competitionEnd(event), range.start, range.end),
		)
		.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
};

/** First competition whose end is on/after `fromISO`. */
export const findNextCompetition = (competitions, fromISO) =>
	(competitions || [])
		.filter((event) => competitionEnd(event) >= fromISO)
		.sort((a, b) => (a.date || "").localeCompare(b.date || ""))[0] || null;

/**
 * The phase a week belongs to when several overlap: the most specific one,
 * i.e. the shortest.
 *
 * Picking the earliest-starting one instead made a phase drawn inside a longer
 * one invisible — a deload redrawn over a peak block that still ran to the old
 * date never showed up in the band, however many times it was re-created. Ties
 * go to the one that starts later, then to agenda order.
 */
export const primaryMesocycle = (phases) =>
	(phases || []).reduce((best, phase) => {
		if (!best) return phase;
		const span = daysBetween(phase.start_date, mesocycleEnd(phase));
		const bestSpan = daysBetween(best.start_date, mesocycleEnd(best));
		if (span !== bestSpan) return span < bestSpan ? phase : best;
		return (phase.start_date || "") > (best.start_date || "") ? phase : best;
	}, null);

/**
 * Contiguous runs of weeks sharing the same primary mesocycle, as
 * `{ mesocycle, startIndex, span }`. Overlapping phases stay available through
 * `mesocyclesForRange` for the tooltip and the context chips. Undated weeks
 * sit where `getWeekCalendarRanges` projects them; weeks without a phase (or
 * with nothing to be projected from) break the run.
 */
export const buildSeasonSegments = (weeks, mesocycles) => {
	const segments = [];
	let current = null;
	getWeekCalendarRanges(weeks).forEach((range, index) => {
		const phases = mesocyclesForRange(mesocycles, range);
		const primary = primaryMesocycle(phases);
		if (!primary) {
			current = null;
			return;
		}
		if (current && current.mesocycle.mesocycle_id === primary.mesocycle_id) {
			current.span += 1;
			return;
		}
		current = { mesocycle: primary, startIndex: index, span: 1 };
		segments.push(current);
	});
	return segments;
};

/**
 * Days the weeks after `weekIndex` have to move back to close the hole left by
 * deleting it: the distance between its calendar week and the next dated one,
 * so a program that already had a gap there keeps it. `0` when either end is
 * undated and there is nothing to line up.
 */
export const weekGapAfterDelete = (weeks, weekIndex) => {
	const removed = getWeekCalendarRange((weeks || [])[weekIndex]);
	if (!removed) return 0;
	const next = (weeks || [])
		.slice(weekIndex + 1)
		.map(getWeekCalendarRange)
		.find(Boolean);
	if (!next) return 0;
	return Math.max(0, daysBetween(removed.start, next.start));
};

/** Every athlete the program reaches: flat ids plus explicit assignments. */
export const getSeasonAthleteIds = (program) => {
	const ids = new Set(program?.athlete_ids || []);
	(program?.athlete_assignments || []).forEach((assignment) => {
		if (assignment?.athlete_id) ids.add(assignment.athlete_id);
	});
	return Array.from(ids);
};
